import moment, { Moment } from "moment";
import { DateRange } from "../types";
import { DateRangeOptions } from "../enums";
import { CALENDAR_DEFAULT_TIMEZONE } from "../constants";

export const formatDate = (
  date: string | moment.Moment | number,
  format: string = "YYYY-MM-DD",
): string => {
  return moment(date).format(format);
};

export const getDateRange = (range: DateRangeOptions | string): DateRange => {
  const startOfToday = moment().startOf("day");
  const endOfToday = moment().endOf("day");

  switch (range) {
    case "today":
      return {
        startDate: startOfToday.toISOString(),
        endDate: endOfToday.toISOString(),
      };
    case "yesterday":
      const yesterday = moment().subtract(1, "days");
      const yesterdayStart = yesterday.startOf("day").toISOString();
      const yesterdayEnd = yesterday.endOf("day").toISOString();

      return {
        startDate: yesterdayStart,
        endDate: yesterdayEnd,
      };
    case "thisWeek":
      const thisWeekStart = moment().startOf("isoWeek").toISOString();
      const thisWeekEnd = moment().endOf("isoWeek").toISOString();

      return {
        startDate: thisWeekStart,
        endDate: thisWeekEnd,
      };
    case "lastWeek":
      const lastWeek = moment().subtract(1, "weeks");
      const lastWeekStart = lastWeek.startOf("isoWeek").toISOString();
      const lastWeekEnd = lastWeek.endOf("isoWeek").toISOString();

      return {
        startDate: lastWeekStart,
        endDate: lastWeekEnd,
      };
    case "thisMonth":
      const thisMonthStart = moment().startOf("month").toISOString();
      const thisMonthEnd = moment().endOf("month").toISOString();

      return {
        startDate: thisMonthStart,
        endDate: thisMonthEnd,
      };
    case "lastMonth":
      const lastMonth = moment().subtract(1, "months");
      const lastMonthStart = lastMonth.startOf("month").toISOString();
      const lastMonthEnd = lastMonth.endOf("month").toISOString();

      return {
        startDate: lastMonthStart,
        endDate: lastMonthEnd,
      };
    case "thisQuarter":
      const thisQuarterStart = moment().startOf("quarter").toISOString();
      const thisQuarterEnd = moment().endOf("quarter").toISOString();

      return {
        startDate: thisQuarterStart,
        endDate: thisQuarterEnd,
      };
    case "lastQuarter":
      const lastQuarter = moment().subtract(1, "quarters");
      const lastQuarterStart = lastQuarter.startOf("quarter").toISOString();
      const lastQuarterEnd = lastQuarter.endOf("quarter").toISOString();

      return {
        startDate: lastQuarterStart,
        endDate: lastQuarterEnd,
      };
    case "last6Months":
      const last6Months = moment().subtract(6, "months");
      const last6MonthsStart = last6Months.startOf("day").toISOString();
      const last6MonthsEnd = endOfToday.toISOString();

      return {
        startDate: last6MonthsStart,
        endDate: last6MonthsEnd,
      };
    case "last12Months":
      const last12Months = moment().subtract(12, "months");
      const last12MonthsStart = last12Months.startOf("day").toISOString();
      const last12MonthsEnd = endOfToday.toISOString();

      return {
        startDate: last12MonthsStart,
        endDate: last12MonthsEnd,
      };
    case "yearToDate":
      const yearToDateStart = moment().startOf("year").toISOString();
      const yearToDateEnd = moment().endOf("year").toISOString();

      return {
        startDate: yearToDateStart,
        endDate: yearToDateEnd,
      };
    case "thisYear":
      const thisYearStart = moment().startOf("year").toISOString();
      const thisYearEnd = moment().endOf("year").toISOString();

      return {
        startDate: thisYearStart,
        endDate: thisYearEnd,
      };
    default:
      return {
        startDate: "",
        endDate: "",
      };
  }
};

export const getDate = (date: string): string => {
  const today = moment().startOf("day");

  switch (date) {
    case "today":
      return formatDate(today);
    case "yesterday":
      const yesterday = formatDate(moment().subtract(1, "days").startOf("day"));
      return formatDate(yesterday);
    default:
      return "";
  }
};

export const utcStringToMoment = (dateString: string): Moment => {
  return moment(dateString);
};

export const addDays = (date: string | moment.Moment, days: number) => {
  return moment(date).add(days, "d");
};

export const hasDatePassed = (date: string | moment.Moment) => {
  const inputDate = moment(date);
  const today = moment().startOf("day");

  return inputDate.isBefore(today);
};

export const isValidDate = (date: string): boolean => {
  const parsedDate = new Date(date);
  const currentDate = new Date();
  return !isNaN(parsedDate.getTime()) && parsedDate <= currentDate;
};

const ZONED_PARTS_FORMAT: Intl.DateTimeFormatOptions = {
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};

const getZonedFormatter = (timeZone: string): Intl.DateTimeFormat => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      ...ZONED_PARTS_FORMAT,
      timeZone,
    });
  } catch {
    return new Intl.DateTimeFormat("en-US", {
      ...ZONED_PARTS_FORMAT,
      timeZone: CALENDAR_DEFAULT_TIMEZONE,
    });
  }
};

const getZonedParts = (date: Date, timeZone: string) => {
  const parts = getZonedFormatter(timeZone).formatToParts(date);
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "00";

  return {
    year: getPart("year"),
    month: getPart("month"),
    day: getPart("day"),
    hour: getPart("hour"),
    minute: getPart("minute"),
    second: getPart("second"),
  };
};

const getTimeZoneOffsetMs = (date: Date, timeZone: string): number => {
  const parts = getZonedParts(date, timeZone);
  const zonedAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );

  return zonedAsUtc - Math.floor(date.getTime() / 1000) * 1000;
};

export const zonedDateTimeToUtcIso = (
  date: string,
  time: string,
  timeZone: string,
): string => {
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  const localAsUtc = Date.UTC(year, month - 1, day, hours, minutes);
  const firstGuess =
    localAsUtc - getTimeZoneOffsetMs(new Date(localAsUtc), timeZone);
  const utc = localAsUtc - getTimeZoneOffsetMs(new Date(firstGuess), timeZone);

  return new Date(utc).toISOString();
};

export const utcToZonedDateTime = (
  utcIso: string,
  timeZone: string,
): { date: string; time: string } => {
  const parts = getZonedParts(new Date(utcIso), timeZone);

  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
};

export const getZonedDateRangeUtc = (
  startDate: string,
  endDate: string,
  timeZone: string,
): { startDate: string; endDate: string } => {
  const nextDay = moment(endDate).add(1, "day").format("YYYY-MM-DD");

  return {
    startDate: zonedDateTimeToUtcIso(startDate, "00:00", timeZone),
    endDate: new Date(
      new Date(zonedDateTimeToUtcIso(nextDay, "00:00", timeZone)).getTime() - 1,
    ).toISOString(),
  };
};

export const getZonedToday = (timeZone: string): string =>
  utcToZonedDateTime(new Date().toISOString(), timeZone).date;
